<template>
    <div>
        <config-form-builder v-if="loaded" />
        <loading v-else />

        <!-- Unsaved Changes Checker -->
        <unsaved-checker
            ref="unsavedChecker"
            :has-unsaved-changes="hasUnsavedChanges"
            :skip-before-unload="skipBeforeUnload"
        />
    </div>
</template>
<script>
import { call, get } from "vuex-pathify";
import ConfigFormBuilder from "Components/ConfigFormBuilder/index.vue";
import Loading from "Components/ConfigFormBuilder/components/Loading.vue";
import UnsavedChecker from "Modules/ConfigPageBuilders/UnsavedChecker";
import unsavedChangesMixin from "Modules/ConfigPageBuilders/UnsavedChecker/unsavedChangesMixin";
import lodashIsEqual from "lodash/isEqual";
import lodashCloneDeep from "lodash/cloneDeep";

export default {
    name: "FormPage",
    components: { ConfigFormBuilder, Loading, UnsavedChecker },
    mixins: [unsavedChangesMixin],
    props: {
        dealerIds: {
            type: String,
            required: false,
            default: null,
        },
        page: {
            type: String,
            required: true,
        },
    },
    computed: {
        userId: get("loggedInUser/userId"),
        selectedDealerId: get("loggedInUser/selectedDealer@id"),
        builderLoader: get("pageConfigs/loader"),
        defaultsLoader: get("pageConfigs/form@loader"),
        formData: get("pageConfigs/form@data"),
        formDefaults: get("pageConfigs/form@defaults"),
        formInitData: get("pageConfigs/form@initData"),
        tmp: get("pageConfigs/tmp"),
        loaded() {
            return this.builderLoader.isComplete && this.defaultsLoader.isComplete;
        },
        dealerId() {
            return this.dealerIds || this.selectedDealerId;
        },
        skipBeforeUnload() {
            return !!(this.tmp && this.tmp.banners && this.tmp.banners.skipBeforeUnload);
        },
        hasUnsavedChanges() {
            // Don't show unsaved changes while loading
            if (!this.loaded) return false;
            if (!this.formData || !this.formDefaults) return false;

            // If formData is empty (no changes made yet), no unsaved changes
            if (Object.keys(this.formData).length === 0) return false;

            // If initFormData has been captured (e.g., for Banners page with initialization logic),
            // compare against it to prevent false positives from initialization
            if (this.formInitData && Object.keys(this.formInitData).length > 0) {
                // Normalize to exclude auto-generated fields (e.g., banner HTML)
                const normalizedFormData = this.normalizeFormDataForComparison(this.formData);
                const normalizedInitData = this.normalizeFormDataForComparison(this.formInitData);

                // Use order-independent comparison for arrays
                const hasChanges = !this.isEqualIgnoringArrayOrder(normalizedFormData, normalizedInitData);
                return hasChanges;
            }

            // Otherwise, fall back to comparing with formDefaults (for pages without initialization logic)
            // Get keys that exist in both formData and formDefaults
            const formDataKeys = Object.keys(this.formData);
            const formDefaultsKeys = Object.keys(this.formDefaults);
            const commonKeys = formDataKeys.filter((key) => formDefaultsKeys.includes(key));

            // If no common keys, no unsaved changes
            if (commonKeys.length === 0) return false;

            // Use order-independent comparison for arrays, deep comparison for others
            return commonKeys.some(
                (key) => !this.isEqualIgnoringArrayOrder(this.formData[key], this.formDefaults[key])
            );
        },
    },
    watch: {
        dealerId: {
            handler(value) {
                if (value) {
                    this.init();
                }
            },
            immediate: true,
        },
    },
    methods: {
        clearFormData: call("pageConfigs/clearFormData"),
        fetchPageBuilder: call("pageConfigs/fetchPageBuilder"),
        fetchDefaults: call("pageConfigs/fetchDefaults"),
        initialFormData(payload) {
            this.clearFormData();
            this.fetchPageBuilder(payload);
            this.fetchDefaults(payload);
        },
        init() {
            const payload = {
                page: this.page,
                dealerId: this.dealerId,
                userId: this.userId,
            };

            this.initialFormData(payload);
        },
        /**
         * Compare two values with special handling for arrays (order-independent comparison).
         * For arrays, checks if they contain the same elements regardless of order.
         * For other types, uses lodash isEqual for deep comparison.
         *
         * @param {*} value1 - First value to compare
         * @param {*} value2 - Second value to compare
         * @returns {boolean} - True if values are equal (arrays compared without order)
         */
        isEqualIgnoringArrayOrder(value1, value2) {
            // If both are arrays, compare as sets (order-independent)
            if (Array.isArray(value1) && Array.isArray(value2)) {
                // Different lengths = different arrays
                if (value1.length !== value2.length) return false;

                // Sort both arrays and compare
                const sorted1 = [...value1].sort();
                const sorted2 = [...value2].sort();
                return lodashIsEqual(sorted1, sorted2);
            }

            // For non-arrays, use standard deep comparison
            return lodashIsEqual(value1, value2);
        },
        /**
         * Normalize form data for comparison by removing auto-generated fields.
         * This prevents false positives in change detection caused by fields that are
         * generated during rendering or other client-side processing.
         *
         * Currently handles:
         * - Banners: Removes 'html' property (generated during Vue rendering cycle)
         */
        normalizeFormDataForComparison(data) {
            // Create a deep clone to avoid mutating the original data
            const normalized = lodashCloneDeep(data);

            // Banners page: Remove auto-generated 'html' property from each banner
            if (normalized.banners && Array.isArray(normalized.banners)) {
                normalized.banners = normalized.banners.map((banner) => {
                    const { html, ...rest } = banner;
                    return rest;
                });
            }

            return normalized;
        },
    },
};
</script>
<style lang="scss" scoped></style>
